import PlayerSowApiService from "@api-services/agency/PlayerSowApiService";

import { LoaderStore } from "@state/stores/common";
import { action, computed, observable, runInAction } from "mobx";
import { RootStore } from "../types";

import { localizationService } from "@state";
import { FieldValues } from "react-hook-form";

import {
	SowSubmissionStatusEnum,
	InputTypeEnum,
	MultiSelectOptionType,
} from "@data-types/sow/PlayerSowEnums";

import {
	AnswersType,
	FileData,
	MappedOptionType,
	MappedQuestionType,
	SubmitDataType,
	UploadDataType,
} from "@data-types/sow/PlayerSowTypes";

import {
	AnswersTypeDto,
	SowQuestionType,
} from "@api-types/sow/PlayerSowApiTypes";
import { DeviceDetector } from "@utils";
import { logger } from "@utils/logger/logger";
import { DateTime } from "luxon";

function createEmptyAnswer(
	id: string,
	abrv: string,
	type: InputTypeEnum
): AnswersType {
	return {
		sowQuestionId: id,
		sowQuestionOptionIds: [],
		customAnswer: "",
		abrv: abrv,
		type: type,
	};
}

export default class PlayerSowStore {
	loader: LoaderStore;
	rootStore: RootStore;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		this.loader = new LoaderStore();
	}

	@observable notes: string = "";
	@observable files: File[] = [];
	@observable questions: SowQuestionType[] = [];
	@observable submissionStatus: number = 0;
	@observable submissionStatusMessage: string | null = null;
	@observable submissionStatusReason: string | null = null;
	@observable answers: AnswersType[] = [];

	maxSize = 5 * 1024 * 1024;
	allowedTypes = [
		"image/jpeg",
		"image/png",
		"image/jpg",
		"application/pdf",
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		"text/plain",
	];

	@computed get isSubmissionRequired() {
		return (
			this.submissionStatus ===
				SowSubmissionStatusEnum.FullSubmissionRequired ||
			this.submissionStatus ===
				SowSubmissionStatusEnum.AdditionalDocumentsRequired
		);
	}

	@computed get isFormRequired() {
		return (
			this.submissionStatus ===
			SowSubmissionStatusEnum.FullSubmissionRequired
		);
	}

	@computed get doesActiveSowRequestExists() {
		return (
			this.submissionStatus ===
			SowSubmissionStatusEnum.NoActiveSOWRequests
		);
	}

	@computed get isSowRequestAlreadyCompleted() {
		return (
			this.submissionStatus ===
			SowSubmissionStatusEnum.DocumentsAlreadySubmitted
		);
	}

	@computed get mappedQuestions() {
		const mappedQuestions: MappedQuestionType[] = this.questions.map(
			(question) => {
				let type: InputTypeEnum = InputTypeEnum.Text;
				let abrv: string = "";
				let isCustomRequired: boolean = false;

				switch (question.sortOrder) {
					case 1:
						type = InputTypeEnum.Text;
						abrv = "firstAndLastName";
						break;
					case 2:
						type = InputTypeEnum.Text;
						abrv = "userName";
						break;
					case 3:
						type = InputTypeEnum.Date;
						abrv = "birthDate";
						break;
					case 4:
						type = InputTypeEnum.Text;
						abrv = "address";
						break;
					case 5:
						type = InputTypeEnum.CheckboxOnly;
						abrv = "monthlyIncome";
						break;
					case 6:
						type = InputTypeEnum.CheckboxCustomOne;
						abrv = "professionalStatus";
						isCustomRequired = true;
						break;
					case 7:
						type = InputTypeEnum.CheckboxCustomMultiple;
						abrv = "assets";
						break;
					case 8:
						type = InputTypeEnum.CheckboxCustomMultiple;
						abrv = "documentsUploaded";
						break;
					default:
						break;
				}

				if (
					type === InputTypeEnum.CheckboxCustomOne ||
					type === InputTypeEnum.CheckboxCustomMultiple
				) {
					const mappedOptions: MappedOptionType[] =
						question.sowQuestionOptions.map((option) => {
							if (
								option.sortOrder ==
								question.sowQuestionOptions.length
							) {
								return {
									...option,
									type: MultiSelectOptionType.Text,
								};
							} else {
								return {
									...option,
									type: MultiSelectOptionType.Checkbox,
								};
							}
						});

					return {
						...question,
						abrv: abrv,
						type: type,
						mappedOptions: mappedOptions,
						isCustomRequired: isCustomRequired,
					};
				}

				if (type === InputTypeEnum.CheckboxOnly) {
					const mappedOptions: MappedOptionType[] =
						question.sowQuestionOptions.map((option) => {
							return {
								...option,
								type: MultiSelectOptionType.Checkbox,
							};
						});

					return {
						...question,
						abrv: abrv,
						type: type,
						mappedOptions: mappedOptions,
						isCustomRequired: isCustomRequired,
					};
				}

				return {
					...question,
					abrv: abrv,
					type: type,
					isCustomRequired: isCustomRequired,
				};
			}
		);

		return mappedQuestions;
	}

	@computed get fileData(): FileData[] {
		const fileData: FileData[] = this.files.map((file) => ({
			filename: file.name,
		}));

		return fileData;
	}

	@action.bound
	async onInitialize(requestId: string, userId: string) {
		this.loader.suspend();

		await this.fetchSubmissionStatus(requestId, userId);
	}

	@action.bound
	async fetchQuestions() {
		this.loader.suspend();
		let items: SowQuestionType[] = [];

		try {
			const response = await PlayerSowApiService.getSowQuestions();

			response.item.map((question) => {
				items.push(question);
			});

			runInAction(() => {
				this.questions = items;
			});
		} catch (error) {
			logger.logError(error, {
				level: "error",
				extra: { message: "Error fetching SOW questions" },
			});
			console.error(error);

			this.submissionStatus = 5;
			const errorMessage = localizationService.t("ERROR.MESSAGE");
			this.submissionStatusMessage = errorMessage;
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	initializeAnswers(questions: MappedQuestionType[]) {
		let emptyAnswers: AnswersType[] = [];

		questions.map((question) => {
			emptyAnswers.push(
				createEmptyAnswer(question.id, question.abrv, question.type)
			);
		});

		this.answers = emptyAnswers;
	}

	@action.bound
	async fetchSubmissionStatus(requestId: string, userId: string) {
		this.loader.suspend();

		try {
			const response = await PlayerSowApiService.getSowSubmissionStatus(
				requestId,
				userId
			);

			runInAction(() => {
				this.submissionStatus = response.sowSubmissionStatus;
				this.submissionStatusMessage = response.errorMessage;
				this.submissionStatusReason = response.reason;
			});
		} catch (error) {
			console.error(error);
			logger.logError(error, {
				level: "error",
				extra: { message: "Error fetching SOW submission status" },
			});
			this.submissionStatus = 5;
			const errorMessage = localizationService.t("ERROR.MESSAGE");
			this.submissionStatusMessage = errorMessage;
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	setLoading(value: boolean) {
		if (value === true) {
			this.loader.suspend();
		} else {
			this.loader.resume();
		}
	}

	@action.bound
	setFiles(newFiles: File[]) {
		newFiles.map((file) => {
			this.files.push(file);
		});
	}

	@action.bound
	removeFile(id: number) {
		this.files = this.files.filter((_, idx) => idx != id);
	}

	@action.bound
	setNotes(value: string) {
		this.notes = value;
	}

	@action.bound
	handleAnswers(data: FieldValues): AnswersTypeDto[] {
		const updatedAnswers: AnswersTypeDto[] = this.answers.map((answer) => {
			// this one should be changed after deploy
			if (answer.abrv === "professionalStatus") {
				let optionIds: string[] = data[answer.abrv];

				this.mappedQuestions.forEach((q) => {
					q.mappedOptions?.forEach((option) => {
						if (option.abrv === "company-employer") {
							optionIds.push(option.id);
						}
					});
				});

				return {
					sowQuestionId: answer.sowQuestionId,
					customAnswer: data[`${answer.abrv}-custom`],
					sowQuestionOptionIds: optionIds,
				};
			}

			if (answer.type === InputTypeEnum.Text) {
				return {
					sowQuestionId: answer.sowQuestionId,
					customAnswer: data[answer.abrv],
					sowQuestionOptionIds: answer.sowQuestionOptionIds,
				};
			}

			if (answer.type === InputTypeEnum.Date) {
				let year;
				let month;
				let day;

				if (DeviceDetector.isMobileTheme) {
					year = data["year"];
					month = data["month"].padStart(2, "0");
					day = data["day"].padStart(2, "0");
				} else {
					// desktop uses date picker so it has different implementation
					const date = DateTime.fromISO(data[answer.abrv]);
					year = date.year;
					month = String(date.month).padStart(2, "0");
					day = String(date.day).padStart(2, "0");
				}

				const dateAnswer = `${year}-${month}-${day}`;

				return {
					sowQuestionId: answer.sowQuestionId,
					customAnswer: dateAnswer,
					sowQuestionOptionIds: answer.sowQuestionOptionIds,
				};
			}

			return {
				sowQuestionId: answer.sowQuestionId,
				customAnswer: data[`${answer.abrv}-custom`],
				sowQuestionOptionIds: data[answer.abrv],
			};
		});

		return updatedAnswers;
	}

	@action.bound
	async onSubmitDocumentsOnly(submitParams: UploadDataType) {
		const reqObject: SubmitDataType = {
			requestId: submitParams.requestId,
			userId: submitParams.userId,
			files: this.files,
			message: this.notes,
			fileData: this.fileData,
			answers: [],
			isAccepted: true,
		};

		try {
			this.loader.suspend();
			await PlayerSowApiService.submitSow(reqObject);
			this.rootStore.notificationStore.success(
				localizationService.t("PLAYER_SOW.UPLOAD_SUCCESS")
			);
		} catch (error) {
			if (error.data && error.data.errorMessage) {
				console.error(error.data);
				this.rootStore.notificationStore.error(error.data.errorMessage);
			}
		} finally {
			this.onDispose();
			this.fetchSubmissionStatus(
				submitParams.requestId,
				submitParams.userId
			);
			this.loader.resume();
		}
	}

	@action.bound
	async onSubmitFullForm(data: FieldValues, submitParams: UploadDataType) {
		const answers = this.handleAnswers(data);

		const reqObject: SubmitDataType = {
			requestId: submitParams.requestId,
			userId: submitParams.userId,
			files: this.files,
			message: this.notes,
			fileData: this.fileData,
			answers: answers,
			isAccepted: data["confirmCheckbox"] !== undefined ? true : false,
		};

		try {
			this.loader.suspend();
			await PlayerSowApiService.submitSow(reqObject);
			this.rootStore.notificationStore.success(
				localizationService.t("PLAYER_SOW.UPLOAD_SUCCESS")
			);
		} catch (error) {
			if (error.data && error.data.errorMessage) {
				console.error(error.data);
				this.rootStore.notificationStore.error(error.data.errorMessage);
			}
		} finally {
			this.onDispose();
			this.fetchSubmissionStatus(
				submitParams.requestId,
				submitParams.userId
			);
			this.loader.resume();
		}
	}

	@action.bound
	onDispose() {
		this.notes = "";
		this.files = [];
		this.questions = [];
		this.submissionStatus = 0;
		this.submissionStatusMessage = null;
		this.answers = [];
	}
}
